import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useMemo } from 'react';

interface CommandCenterFiltersProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  showActiveOnly: boolean;
  onActiveOnlyChange: (active: boolean) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export const CommandCenterFilters: React.FC<CommandCenterFiltersProps> = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  showActiveOnly,
  onActiveOnlyChange,
  page,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
}) => {
  // Debounced search handler
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e.target.value);
    },
    [onSearchChange],
  );

  // Keyboard navigation for pagination
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Fix: Ensure keyboard navigation works only when the event is from the intended element and keys are handled robustly
      if (!(e.target instanceof HTMLElement) || e.target !== e.currentTarget)
        return;

      const { key } = e;

      if (key === 'ArrowLeft' && page > 1) {
        e.preventDefault();
        onPageChange(page - 1);
      } else if (key === 'ArrowRight' && page < totalPages) {
        e.preventDefault();
        onPageChange(page + 1);
      } else if (key === 'Home') {
        e.preventDefault();
        onPageChange(1);
      } else if (key === 'End') {
        e.preventDefault();
        onPageChange(totalPages);
      }
    },
    [page, totalPages, onPageChange],
  );

  // Selection summary
  const selectionSummary = useMemo(() => {
    if (selectedCount === 0) return null;
    if (selectedCount === totalCount) return 'All selected';
    return `${selectedCount} of ${totalCount} selected`;
  }, [selectedCount, totalCount]);

  return (
    <div className="bg-white border-b border-gray-200">
      {/* Main filter row */}
      <div className="flex items-center gap-3 px-4 py-2">
        {/* Search input */}
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search matters, tasks, entities..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            autoComplete="off"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Active only toggle */}
        <label
          htmlFor="active-only-checkbox"
          className="flex items-center gap-2 text-xs cursor-pointer"
        >
          <input
            id="active-only-checkbox"
            type="checkbox"
            checked={showActiveOnly}
            onChange={(e) => onActiveOnlyChange(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Active only
        </label>

        {/* Selection controls */}
        {selectedCount > 0 && (
          <div className="flex items-center gap-2 px-2 py-1 bg-blue-50 rounded border-l-2 border-blue-400">
            <span className="text-xs font-medium text-blue-800">
              {selectionSummary}
            </span>
            <button
              type="button"
              onClick={onClearSelection}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Clear
            </button>
          </div>
        )}

        {/* Bulk select */}
        <button
          type="button"
          onClick={
            selectedCount === totalCount ? onClearSelection : onSelectAll
          }
          className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 border border-gray-300 rounded"
        >
          {selectedCount === totalCount ? 'Deselect All' : 'Select All'}
        </button>

        {/* Keyboard shortcut hint */}
        <div className="text-xs text-gray-500 ml-auto">
          <kbd className="bg-gray-100 px-1 rounded">⌘K</kbd> search
          <span className="mx-2">•</span>
          <kbd className="bg-gray-100 px-1 rounded">←→</kbd> navigate
        </div>
      </div>

      {/* Pagination row */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-gray-300 rounded px-1 py-0.5"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span>per page</span>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => onPageChange(1)}
            disabled={page <= 1}
            className="px-2 py-0.5 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            title="First page (Home)"
          >
            ⇤
          </button>
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="px-2 py-0.5 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            title="Previous page (←)"
          >
            ←
          </button>

          <span className="px-2 py-0.5 font-mono">
            {page} of {totalPages}
          </span>

          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="px-2 py-0.5 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            title="Next page (→)"
          >
            →
          </button>
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            disabled={page >= totalPages}
            className="px-2 py-0.5 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            title="Last page (End)"
          >
            ⇥
          </button>
        </div>

        <div className="text-xs text-gray-500 font-mono">
          Showing {(page - 1) * pageSize + 1}-
          {Math.min(page * pageSize, totalCount)} of {totalCount}
        </div>
      </div>
    </div>
  );
};
